import OpenEngineerApi from '@/api/OpenEngineerApi';
import OpenServiceDeskApi from '@/api/OpenServiceDeskApi';
import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule
} from 'vuex-module-decorators';
import store from '../index';

interface RoleType {
  id: number,
  name: string,
  nameKey?: string
}

export interface ServiceDeskType {
  isDefault: boolean,
  id: number,
  name: string,
  engineerCount: number,
  parentId?: number 
  status: number, // 客服组启用状态：1启用，0停用
}

interface ServiceDeskParamsType {
  page?: {
    pageNumber: number, 
    pageSize: number
  },
  showEngineerCount?: boolean,
  key?: string,
  serviceDeskStatus?: string
}

export interface AccountState {
  roleList: Array<RoleType>,
  serviceDeskList: Array<ServiceDeskType>
}

function translate2Tree (arr, pId?) {
  const ret:any[] = []
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].parentId === pId) {
      arr[i].children = translate2Tree(arr, arr[i].id)
      ret.push(arr[i])
    }
  }
  return ret
}

// 判断是否是最顶级的节点
function checkIsTop (arr, pid) {
  if (!pid) {
    return true
  }
  const target = arr.find(item => item.id === pid)
  if (!target) {
    return true
  }
  return false
}
// 根据pid找到所有子节点
function findChildByPid (arr:any[], pid?:number, parentItem?:any) {
  const children: any[] = []
  let sum = 0
  for (let i = 0; i < arr.length; i++) {
    const target = arr[i]
    if (target.parentId === pid) {
      arr.splice(i, 1);
      i -= 1;
      // 如果存在父级数据，在父status=0的情况下，优先使用父的status
      if (parentItem) {
        target.status = parentItem?.status === 0 ? 0 : target.status;
      }
      const res = findChildByPid(arr, target.id, target)
      target.children = res.children
      target.engineerCount += res.sum
      sum += target.engineerCount
      children.push(target)
    }
  }
  return {
    children,
    sum
  }
}

export function arr2TreeList (arr) {
  const result: any[] = []
  for (let i = 0; i < arr.length; i++) {
    const isTop = checkIsTop(arr, arr[i].parentId)
    if (isTop) {
      const res = findChildByPid([...arr], arr[i].id, arr[i]);
      arr[i].children = res.children
      arr[i].engineerCount += res.sum
      result.push(arr[i])
    }
  }
  return result
}

@Module({ name: 'Account', dynamic: true, namespaced: true, store })
class Account extends VuexModule implements AccountState {
  roleList: Array<RoleType> = []
  serviceDeskList: Array<ServiceDeskType> = []

  get serviceDeskTreeList () {
    const list = JSON.parse(JSON.stringify(this.serviceDeskList))
    return arr2TreeList(list)
  }

  get serviceDeskTreeListWidthExcluedId () {
    return id => {
      const list = this.serviceDeskList.filter(item => item.id !== id)
      const listClone = JSON.parse(JSON.stringify(list))
      return arr2TreeList(listClone)
    }
  }

  get serviceDeskTreeListWidthKewword () {
    return keyword => {
      const list = this.serviceDeskList.filter(item => item.name.includes(keyword))
      const listClone = JSON.parse(JSON.stringify(list))
      return arr2TreeList(listClone)
    }
  }

  @Mutation
  private SET_ROLE_LIST (list: Array<RoleType>) {
    this.roleList = list
  }

  @Mutation
  private SET_SERVICE_DESK_LIST (list: Array<ServiceDeskType>) {
    this.serviceDeskList = list
  }

  /**
   * @description 获取角色列表
   * */ 
   @Action
   public loadRoleList () {
      OpenEngineerApi.listRole()
      .then(res => {
        this.SET_ROLE_LIST(res.list)
      })
   }

  /**
   * @description 获取组织结构数据
   * */ 
  @Action
  public loadServiceDeskList (param?:ServiceDeskParamsType) {
    let params = {
      page: {
        pageNumber: 1, 
        pageSize: 99999
      },
      showEngineerCount: true,
      key: ''
    };
    if (param) {
      params = { ...params, ...param };
    }
    OpenServiceDeskApi.listServiceDesk(params).then(res => {
      const list = res.list.sort((a, b) => {
        if (a.orderKey < b.orderKey) {
          return -1
        }
        if (a.orderKey === b.orderKey) {
          return 0
        }
        if (a.orderKey > b.orderKey) {
          return 1
        }
      })
      this.SET_SERVICE_DESK_LIST(list);
    })
  }
  
  /**
   * @description 获取组织结构数据，直接返回数据，不存储在store中
   * */ 
  @Action
  public getServiceDeskList (param?:ServiceDeskParamsType) {
    let params = {
      page: {
        pageNumber: 1, 
        pageSize: 99999
      },
      showEngineerCount: true,
      key: ''
    };
    if (param) {
      params = { ...params, ...param };
    }
    return new Promise((resolve, reject) => {
      return OpenServiceDeskApi.listServiceDesk(params).then(res => {
        const list:Array<ServiceDeskType> = res.list.sort((a, b) => {
          if (a.orderKey < b.orderKey) {
            return -1
          }
          if (a.orderKey === b.orderKey) {
            return 0
          }
          if (a.orderKey > b.orderKey) {
            return 1
          }
        })
        resolve(list);
      });
    });
  }

  @Action
  public deleteServiceDeskById (id: number) {
    const index = this.serviceDeskList.findIndex(item => item.id === id)
    if (index > -1) {
      this.serviceDeskList.splice(index, 1)
    }
  }

  /**
   * @description 获取角色列表
   * */ 
  @Action
  public loadServiceList (query: any) {
    return OpenEngineerApi.getEngineers(query)
    .then(res => {
      return res.users || []
    })
  }
}

export const AccountModule = getModule(Account);
